import type { DexTickLiquidity } from "../hooks/useDexTickLiquidity.ts";

// Pure transform from the endpoint payload to chart-ready density bars.
//
// The backend emits non-overlapping tick segments [bps_lower, bps_upper) — the
// sweep-line partition where active liquidity is constant between adjacent
// initialized ticks. We plot LIQUIDITY DENSITY, not total value: a wide diffuse
// segment and a narrow concentrated one with the same dollars must NOT draw at
// the same height, or the wide one visually blankets the narrow one (which is
// what "value parked" did on mixed-width pools like BNKR/WETH).
//
//   density = segment_usd / (bps_width / 100)   // USD per 1% price move
//
// Density is CLIP-INVARIANT under the segment's uniform-liquidity assumption:
// clipping to the visible domain scales visible_usd and visible_width by the
// same factor, so density from raw bounds == density from clipped bounds. We
// compute density from raw bounds and only clip the drawn x-extent. Bar AREA
// (density x width) stays proportional to dollar value.

export interface TickBar {
  bpsLower: number; // clipped to +/-clipBps, for drawing
  bpsUpper: number;
  rawBpsLower: number; // true segment bounds, for the tooltip range
  rawBpsUpper: number;
  density: number; // USD per 1% price move (bar height)
  value: number; // plotted bar height = visibleUsd (LIQ-130); density kept separately
  isActive: boolean; // the segment containing current price (authoritative)
  clipped: boolean; // segment extends beyond the visible domain
  tick: number;
  nextTick: number | null;
  priceLower: number | null; // base/quote at tick_index
  priceUpper: number | null; // base/quote at next initialized tick
  amountBase: number | null;
  amountQuote: number | null;
  valueUsd: number | null; // total USD in the full segment
  visibleUsd: number | null; // USD in the visible (clipped) portion
}

export interface TickBars {
  bars: TickBar[];
  omitted: number; // segments fully outside the visible domain
  clipBps: number;
  maxDensity: number; // tallest bar (for the y-axis)
  usdAbove: number;
  usdBelow: number;
  ethUsd: number | null;
  ethUsdAgeSeconds: number | null;
  usdPerBase: number | null;
  basePerQuote: number | null;
  quoteUsdRate: number | null;
  baseAsset: string;
  quoteAsset: string;
}

export function toTickBars(resp: DexTickLiquidity, clipBps = 9000): TickBars {
  const bars: TickBar[] = [];
  let omitted = 0;
  let maxDensity = 0;
  let usdAbove = 0;
  let usdBelow = 0;
  const ethUsd = resp.eth_usd;
  const [baseAsset, quoteAsset] = (resp.pair || "/").split("/");

  // Reconstruct each segment's UPPER bound from tick adjacency, not from the
  // emitted t.bps_upper. The backend derives bps_upper by extrapolating
  // price(tick + spacing) across (next_tick - tick_index); over wide tick gaps
  // that overshoots and makes segments overlap. The true upper price of a
  // segment is the price at its next_tick -- i.e. the bps_lower of the row whose
  // tick_index == this row's next_tick. Segments then tile by construction.
  const bpsByTick = new Map<number, number>();
  for (const t of resp.ticks) {
    if (t.bps_lower != null && Number.isFinite(t.bps_lower)) {
      bpsByTick.set(t.tick_index, t.bps_lower);
    }
  }

  for (const t of resp.ticks) {
    // Above/below USD split over ALL ticks (quote token below price, base above).
    if (ethUsd != null && t.value_usd != null && t.amount_quote != null) {
      const below = t.amount_quote * ethUsd;
      const above = t.value_usd - below;
      usdBelow += below;
      usdAbove += above > 0 ? above : 0;
    }

    // Segment bounds = { this tick's bps, next_tick's bps }, ordered.
    // Fall back to the stored (narrow) bps_upper only if next_tick is absent
    // from the emitted set (last-in-chain rail); those get clipped out anyway.
    const myBps = t.bps_lower;
    const nextBps =
      t.next_tick != null && bpsByTick.has(t.next_tick)
        ? (bpsByTick.get(t.next_tick) as number)
        : t.bps_upper;
    const bl = myBps != null && nextBps != null ? Math.min(myBps, nextBps) : null;
    const bu = myBps != null && nextBps != null ? Math.max(myBps, nextBps) : null;
    const value = t.value_usd;
    if (bl == null || bu == null || value == null || !Number.isFinite(value)) {
      omitted += 1;
      continue;
    }
    const rawWidth = bu - bl;
    if (rawWidth <= 0) {
      omitted += 1;
      continue;
    }
    // Keep any segment that OVERLAPS the visible domain; clip its drawn extent.
    const clippedLower = Math.max(bl, -clipBps);
    const clippedUpper = Math.min(bu, clipBps);
    if (clippedUpper <= clippedLower) {
      omitted += 1; // fully outside the domain
      continue;
    }
    // Clip-invariant density from raw bounds; USD per 1% = per 100 bps.
    const density = value / (rawWidth / 100);
    const visibleWidth = clippedUpper - clippedLower;
    const visibleUsd = value * (visibleWidth / rawWidth);
    const clipped = bl < -clipBps || bu > clipBps;

    if (density > maxDensity) maxDensity = density;
    bars.push({
      bpsLower: clippedLower,
      bpsUpper: clippedUpper,
      rawBpsLower: bl,
      rawBpsUpper: bu,
      density,
      // LIQ-130: plot USD-in-visible-extent (matches the "value parked (USD)"
      // axis + the above/below subtitle), not density. Bar height now sums to
      // the dollar figures; density kept for reference/tooltip parity.
      value: (visibleUsd ?? 0),
      isActive: t.is_active,
      clipped,
      tick: t.tick_index,
      nextTick: t.next_tick,
      priceLower: t.price_lower,
      priceUpper: t.price_upper,
      amountBase: t.amount_base,
      amountQuote: t.amount_quote,
      valueUsd: t.value_usd,
      visibleUsd,
    });
  }
  bars.sort((a, b) => a.bpsLower - b.bpsLower);
  return {
    bars,
    omitted,
    clipBps,
    maxDensity,
    usdAbove,
    usdBelow,
    ethUsd,
    ethUsdAgeSeconds: resp.eth_usd_age_seconds,
    usdPerBase: resp.usd_per_base ?? null,
    basePerQuote: resp.base_per_quote ?? null,
    quoteUsdRate: resp.quote_usd_rate ?? resp.eth_usd ?? null,
    baseAsset: baseAsset || "base",
    quoteAsset: (resp.quote_asset ?? quoteAsset) || "quote",
  };
}
